/**
 * ISMAN Live Content — applies edits made in the admin "Live Page Editor"
 * (admin.isman.co.ke/web-editor?panel=page-editor) to every page of the site.
 *
 * Published edits live in /cms/live.json. When the site is opened inside the
 * admin editor (?isman_edit=1) it also becomes click-to-edit and talks to the
 * editor through postMessage.
 */

export interface StyleOv { color?: string; background?: string; fontSize?: string; fontWeight?: string; fontStyle?: string; textAlign?: string; fontFamily?: string; textTransform?: string; display?: string }
export interface Scope { text: Record<string, string>; img: Record<string, string>; link: Record<string, string>; style: Record<string, StyleOv>; replace: { find: string; replace: string }[] }
export interface ContentDoc {
  version?: number;
  global: Scope;
  pages: Record<string, Scope>;
  theme: { primary?: string; accent?: string; dark?: string; font?: string; headingFont?: string; radius?: string };
}

const ADMIN_ORIGIN = "https://admin.isman.co.ke";
const empty = (): Scope => ({ text: {}, img: {}, link: {}, style: {}, replace: [] });
let doc: ContentDoc = { global: empty(), pages: {}, theme: {} };

const params = new URLSearchParams(location.search);
const EDIT = params.get("isman_edit") === "1" && window.parent !== window;
let mode: "edit" | "browse" = "edit";

const norm = (s: string) => s.replace(/\s+/g, " ").trim();
const pagePath = () => (location.pathname.replace(/\/+$/, "") || "/");

// ---- originals (so edits always apply to the source text) -----------------
const textOrig = new WeakMap<Text, string>();
const lastSet = new WeakMap<Text, string>();
const attrOrig = new WeakMap<Element, Record<string, string>>();

function merged(): Scope {
  const g = doc.global || empty();
  const p = doc.pages?.[pagePath()] || empty();
  return {
    text: { ...g.text, ...p.text },
    img: { ...g.img, ...p.img },
    link: { ...g.link, ...p.link },
    style: { ...g.style, ...p.style },
    replace: [...(g.replace || []), ...(p.replace || [])],
  };
}

function doReplace(s: string, reps: Scope["replace"]) {
  for (const r of reps) if (r.find) s = s.split(r.find).join(r.replace ?? "");
  return s;
}

const SKIP = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "TEXTAREA", "INPUT"]);
const isEditorUi = (el: Element | null) => !!el?.closest?.("[data-isman-ui]");

function origAttr(el: Element, name: string): string | null {
  let o = attrOrig.get(el);
  if (!o) { o = {}; attrOrig.set(el, o); }
  if (!(name in o)) { const v = el.getAttribute(name); if (v === null) return null; o[name] = v; }
  return o[name];
}

let applying = false;
function apply(root: ParentNode = document.body) {
  if (!root) return;
  const m = merged();
  applying = true;
  try {
    // text nodes
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let n: Node | null;
    while ((n = walker.nextNode())) {
      const t = n as Text;
      const parent = t.parentElement;
      if (!parent || SKIP.has(parent.tagName) || isEditorUi(parent) || parent.isContentEditable) continue;
      const cur = t.nodeValue || "";
      // React (or anything else) changed the text since we last touched it -> new original
      if (!textOrig.has(t) || (lastSet.get(t) !== cur && textOrig.get(t) !== cur)) textOrig.set(t, cur);
      const orig = textOrig.get(t)!;
      const key = norm(orig);
      if (!key) continue;
      let val = key in m.text ? m.text[key] : orig;
      val = doReplace(val, m.replace);
      if (key in m.text) { const lead = orig.match(/^\s*/)![0], trail = orig.match(/\s*$/)![0]; val = lead + val.trim() + trail; }
      if (val !== cur) { t.nodeValue = val; }
      lastSet.set(t, t.nodeValue || "");
      // per-text style
      const st = m.style[key];
      if (st) {
        if (!parent.dataset.ismanStyled) parent.dataset.ismanOrigStyle = parent.getAttribute("style") || "";
        parent.setAttribute("style", parent.dataset.ismanOrigStyle || "");
        Object.assign(parent.style, st);
        parent.dataset.ismanStyled = "1";
      } else if (parent.dataset.ismanStyled) {
        const o = parent.dataset.ismanOrigStyle || "";
        if (o) parent.setAttribute("style", o); else parent.removeAttribute("style");
        delete parent.dataset.ismanStyled; delete parent.dataset.ismanOrigStyle;
      }
    }
    // images
    (root as Element).querySelectorAll?.("img").forEach((img) => {
      if (isEditorUi(img)) return;
      const o = origAttr(img, "src"); if (o === null) return;
      const want = m.img[o] ?? o;
      if (img.getAttribute("src") !== want) img.setAttribute("src", want);
      const alt = origAttr(img, "alt");
      if (alt !== null) { const k = norm(alt); const a = doReplace(k in m.text ? m.text[k] : alt, m.replace); if (img.getAttribute("alt") !== a) img.setAttribute("alt", a); }
    });
    // links
    (root as Element).querySelectorAll?.("a[href]").forEach((a) => {
      if (isEditorUi(a)) return;
      const o = origAttr(a, "href"); if (o === null) return;
      const want = doReplace(m.link[o] ?? o, m.replace);
      if (a.getAttribute("href") !== want) a.setAttribute("href", want);
    });
    // placeholders / titles
    (root as Element).querySelectorAll?.("[placeholder],[title]").forEach((el) => {
      for (const at of ["placeholder", "title"]) {
        const o = origAttr(el, at); if (o === null) continue;
        const k = norm(o); const v = doReplace(k in m.text ? m.text[k] : o, m.replace);
        if (el.getAttribute(at) !== v) el.setAttribute(at, v);
      }
    });
  } finally { applying = false; }
}

// ---- theme --------------------------------------------------------------
function themeCss(): string {
  const t = doc.theme || {};
  const out: string[] = [];
  const shades: Record<string, number> = { "300": 45, "400": 22, "500": 8, "600": 0, "700": -18, "800": -35, "900": -50, "950": -65 };
  const mix = (c: string, amt: number) => amt >= 0 ? `color-mix(in srgb, ${c}, white ${amt}%)` : `color-mix(in srgb, ${c}, black ${-amt}%)`;
  const fam = (name: string, c?: string) => {
    if (!c) return;
    for (const [sh, amt] of Object.entries(shades)) {
      const v = mix(c, amt);
      out.push(`.bg-${name}-${sh},.hover\\:bg-${name}-${sh}:hover{background-color:${v}!important}`);
      out.push(`.text-${name}-${sh},.hover\\:text-${name}-${sh}:hover,.group:hover .group-hover\\:text-${name}-${sh}{color:${v}!important}`);
      out.push(`.border-${name}-${sh},.hover\\:border-${name}-${sh}:hover{border-color:${v}!important}`);
      out.push(`.from-${name}-${sh}{--tw-gradient-from:${v} var(--tw-gradient-from-position)!important;--tw-gradient-to:transparent var(--tw-gradient-to-position)!important;--tw-gradient-stops:var(--tw-gradient-from),var(--tw-gradient-to)!important}`);
      out.push(`.to-${name}-${sh}{--tw-gradient-to:${v} var(--tw-gradient-to-position)!important}`);
    }
    out.push(`.bg-${name}-50{background-color:${mix(c, 92)}!important}.bg-${name}-100{background-color:${mix(c, 84)}!important}`);
  };
  fam("teal", t.primary); fam("cyan", t.accent || t.primary);
  if (t.dark) { for (const n of ["slate", "gray"]) { out.push(`.bg-${n}-900{background-color:${t.dark}!important}.bg-${n}-950{background-color:${mix(t.dark, -20)}!important}`); } }
  if (t.font) out.push(`body,button,input,textarea,select{font-family:${t.font}!important}`);
  if (t.headingFont) out.push(`h1,h2,h3,h4{font-family:${t.headingFont}!important}`);
  if (t.radius) out.push(`.rounded-lg,.rounded-xl,.rounded-2xl{border-radius:${t.radius}!important}`);
  return out.join("\n");
}
function applyTheme() {
  let el = document.getElementById("isman-theme") as HTMLStyleElement | null;
  if (!el) { el = document.createElement("style"); el.id = "isman-theme"; document.head.appendChild(el); }
  el.textContent = themeCss();
  const fonts = [doc.theme?.font, doc.theme?.headingFont].filter(Boolean).map((f) => String(f).split(",")[0].replace(/['"]/g, "").trim()).filter((f) => !/^(system-ui|sans-serif|serif|Barlow)$/i.test(f));
  if (fonts.length) {
    let l = document.getElementById("isman-theme-fonts") as HTMLLinkElement | null;
    if (!l) { l = document.createElement("link"); l.id = "isman-theme-fonts"; l.rel = "stylesheet"; document.head.appendChild(l); }
    l.href = "https://fonts.googleapis.com/css2?" + fonts.map((f) => "family=" + encodeURIComponent(f) + ":wght@300;400;600;700;800").join("&") + "&display=swap";
  }
}

// ---- observer --------------------------------------------------------------
let scheduled = false;
function schedule() {
  if (scheduled) return; scheduled = true;
  requestAnimationFrame(() => { scheduled = false; apply(); if (EDIT) reportPage(); });
}
function startObserver() {
  new MutationObserver(() => { if (!applying) schedule(); })
    .observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ["src", "href"] });
  // SPA navigation
  const fire = () => setTimeout(schedule, 30);
  for (const k of ["pushState", "replaceState"] as const) {
    const orig = history[k];
    history[k] = function (this: History, ...args: Parameters<typeof orig>) { const r = orig.apply(this, args); fire(); return r; } as typeof orig;
  }
  addEventListener("popstate", fire);
}

// ---- edit mode (inside the admin iframe) -----------------------------------
let lastPage = "";
function reportPage() {
  if (pagePath() === lastPage) return;
  lastPage = pagePath();
  parent.postMessage({ type: "isman:page", path: lastPage, title: document.title }, ADMIN_ORIGIN);
}

function describe(el: Element): string {
  const tag = el.tagName.toLowerCase();
  const txt = norm(el.textContent || "").slice(0, 24);
  return tag + (txt ? ` "${txt}${(el.textContent || "").length > 24 ? "…" : ""}"` : "");
}

let selected: Element | null = null;
function pathOf(el: Element): { label: string; index: number }[] {
  const chain: Element[] = [];
  let e: Element | null = el;
  while (e && e !== document.body && chain.length < 8) { chain.unshift(e); e = e.parentElement; }
  return chain.map((c, i) => ({ label: describe(c), index: i }));
}
let chainEls: Element[] = [];

function directText(el: Element): Text | null {
  for (const c of Array.from(el.childNodes)) if (c.nodeType === 3 && norm(c.nodeValue || "")) return c as Text;
  return null;
}

function select(el: Element) {
  selected?.classList.remove("isman-sel");
  selected = el; el.classList.add("isman-sel");
  chainEls = []; let e: Element | null = el; while (e && e !== document.body && chainEls.length < 8) { chainEls.unshift(e); e = e.parentElement; }
  const cs = getComputedStyle(el);
  const img = el.tagName === "IMG" ? (el as HTMLImageElement) : null;
  const t = directText(el);
  const link = el.closest("a");
  const texts: { orig: string; value: string }[] = [];
  // all text pieces inside the element (children down to the last item)
  const w = document.createTreeWalker(el, NodeFilter.SHOW_TEXT); let n: Node | null;
  while ((n = w.nextNode()) && texts.length < 40) {
    const tn = n as Text; const o = norm(textOrig.get(tn) ?? tn.nodeValue ?? "");
    if (o && !texts.find((x) => x.orig === o)) texts.push({ orig: o, value: norm(tn.nodeValue || "") });
  }
  const imgs = Array.from(el.querySelectorAll("img")).slice(0, 12).map((i) => ({ orig: origAttr(i, "src") || "", src: i.getAttribute("src") || "", alt: i.getAttribute("alt") || "" }));
  if (img) imgs.unshift({ orig: origAttr(img, "src") || "", src: img.getAttribute("src") || "", alt: img.getAttribute("alt") || "" });
  const links = (link ? [link] : []).concat(Array.from(el.querySelectorAll("a[href]")).slice(0, 12) as HTMLAnchorElement[])
    .filter((a, i, arr) => arr.indexOf(a) === i)
    .map((a) => ({ orig: origAttr(a, "href") || "", href: a.getAttribute("href") || "", text: norm(a.textContent || "").slice(0, 60) }));
  parent.postMessage({
    type: "isman:select",
    path: pagePath(),
    tag: el.tagName.toLowerCase(),
    breadcrumb: pathOf(el),
    textOrig: t ? norm(textOrig.get(t) ?? t.nodeValue ?? "") : null,
    texts, imgs, links,
    style: { color: cs.color, background: cs.backgroundColor, fontSize: cs.fontSize, fontWeight: cs.fontWeight, fontStyle: cs.fontStyle, textAlign: cs.textAlign, fontFamily: cs.fontFamily },
  }, ADMIN_ORIGIN);
}

function inlineEdit(el: Element) {
  const t = directText(el);
  if (!t || el.tagName === "IMG") return;
  const he = el as HTMLElement;
  if (he.children.length > 3) return; // complex block: edit via the panel
  const orig = norm(textOrig.get(t) ?? t.nodeValue ?? "");
  he.contentEditable = "true"; he.focus();
  const done = () => {
    he.removeEventListener("blur", done);
    he.contentEditable = "false";
    const val = norm(t.nodeValue || "");
    if (val !== orig) parent.postMessage({ type: "isman:text", path: pagePath(), orig, value: val }, ADMIN_ORIGIN);
  };
  he.addEventListener("blur", done);
  he.addEventListener("keydown", (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); he.blur(); } if (e.key === "Escape") { t.nodeValue = orig; he.blur(); } });
}

function startEditMode() {
  const css = document.createElement("style");
  css.setAttribute("data-isman-ui", "");
  css.textContent = `
    html.isman-edit [data-isman-hover]{outline:2px dashed #14b8a6!important;outline-offset:2px;cursor:pointer!important}
    html.isman-edit .isman-sel{outline:3px solid #f59e0b!important;outline-offset:2px}
    html.isman-edit [contenteditable=true]{outline:3px solid #22c55e!important;background:rgba(34,197,94,.08)!important;cursor:text!important}
  `;
  document.head.appendChild(css);
  const setMode = (m: "edit" | "browse") => { mode = m; document.documentElement.classList.toggle("isman-edit", m === "edit"); };
  setMode("edit");
  let hov: Element | null = null;
  document.addEventListener("mouseover", (e) => {
    if (mode !== "edit") return;
    hov?.removeAttribute("data-isman-hover");
    hov = e.target as Element; if (hov && hov !== document.body) hov.setAttribute("data-isman-hover", "");
  }, true);
  document.addEventListener("click", (e) => {
    if (mode !== "edit" || e.altKey) return; // Alt+click = use the page normally (tabs, menus)
    const el = e.target as Element;
    if (!el || isEditorUi(el) || (el as HTMLElement).isContentEditable) return;
    e.preventDefault(); e.stopPropagation();
    select(el);
  }, true);
  document.addEventListener("dblclick", (e) => {
    if (mode !== "edit") return;
    e.preventDefault(); e.stopPropagation();
    inlineEdit(e.target as Element);
  }, true);
  // block form submits while editing
  document.addEventListener("submit", (e) => { if (mode === "edit") e.preventDefault(); }, true);

  addEventListener("message", (e) => {
    if (e.origin !== ADMIN_ORIGIN) return;
    const d = e.data || {};
    if (d.type === "isman:doc") { doc = normalise(d.doc); applyTheme(); apply(); if (selected) select(selected); }
    if (d.type === "isman:mode") setMode(d.mode);
    if (d.type === "isman:selectIndex" && chainEls[d.index]) select(chainEls[d.index]);
    if (d.type === "isman:navigate" && typeof d.path === "string") {
      history.pushState({}, "", d.path); dispatchEvent(new PopStateEvent("popstate"));
    }
    if (d.type === "isman:scrollTop") scrollTo({ top: 0, behavior: "smooth" });
  });
  parent.postMessage({ type: "isman:ready", path: pagePath() }, ADMIN_ORIGIN);
}

function normalise(d: Partial<ContentDoc> | null | undefined): ContentDoc {
  // PHP turns empty objects into [] — normalise back to plain objects
  const obj = <T,>(v: unknown): T => (v && typeof v === "object" && !Array.isArray(v) ? (v as T) : ({} as T));
  const fix = (s?: Partial<Scope>): Scope => ({ text: obj(s?.text), img: obj(s?.img), link: obj(s?.link), style: obj(s?.style), replace: Array.isArray(s?.replace) ? s!.replace! : [] });
  const pages: Record<string, Scope> = {};
  for (const [k, v] of Object.entries(obj<Record<string, Scope>>(d?.pages))) pages[k] = fix(v);
  return { version: d?.version, global: fix(d?.global), pages, theme: obj(d?.theme) };
}

export function initLiveContent() {
  const start = () => { applyTheme(); apply(); startObserver(); if (EDIT) { startEditMode(); reportPage(); } };
  if (EDIT) { document.documentElement.classList.remove("isman-cms-wait"); start(); return; } // editor sends the draft itself
  fetch("/cms/live.json?t=" + Math.floor(Date.now() / 60000), { cache: "no-cache" })
    .then((r) => (r.ok ? r.json() : null))
    .then((j) => { if (j) doc = normalise(j); })
    .catch(() => {})
    .finally(() => { if (document.readyState === "loading") addEventListener("DOMContentLoaded", start); else start(); document.documentElement.classList.remove("isman-cms-wait"); });
}
